import { useMemo, useState, useEffect } from 'react';
import {
  Autocomplete,
  TextField,
  FormControl,
  Box,
  Button,
  Stack,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';

const OPTIONS = [
  // { label: 'All Activity', value: 'all' },
  {
    label: 'All Activity',
    value: [
      'postUpdateCreated',
      'advisorJoinedToCompany',
      'surveyCreated',
      'advisorIdea',
      'boardMeetingCreated',
    ],
  },
  { label: 'Introductions', value: 'advisorJoinedToCompany' },
  { label: 'Survey Responses', value: 'surveyCreated' },
  // { label: 'Survey Responses', value: ['surveyResponse', 'surveyCreated'] },
  { label: 'Ideas', value: 'advisorIdea' },
  {
    label: 'Board Updates',
    value: ['postUpdateCreated', 'boardMeetingCreated'],
  },
];
const OPTIONS_WITH_BREAKTHROUGHS = [
  // { label: 'All Activity', value: 'all' },
  {
    label: 'All Activity',
    value: [
      'advisorJoinedToCompany',
      'surveyCreated',
      'advisorIdea',
      'boardMeetingCreated',
    ],
  },
  { label: 'Introductions', value: 'advisorJoinedToCompany' },
  { label: 'Survey Responses', value: 'surveyCreated' },
  // { label: 'Survey Responses', value: ['surveyResponse', 'surveyCreated'] },
  { label: 'Ideas', value: 'advisorIdea' },
  { label: 'Breakthrough Ideas', value: 'breakthroughs' },
  { label: 'Board Meetings', value: 'boardMeetingCreated' },
];

export default function ActivityFilter({
  value = null,
  onChange,
  size = 'medium',
  fullWidth = true,
  sx,
  companyIsP2P,
}) {
  const { accountDetails } = useAccount();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  const showBreakthroughsOption = useMemo(() => {
    return accountDetails?.advisorsWithBreakthroughs?.length > 0;
  }, [accountDetails.advisorsWithBreakthroughs?.length]);

  const finalOptions = useMemo(() => {
    if (showBreakthroughsOption) {
      return OPTIONS_WITH_BREAKTHROUGHS;
    }
    return OPTIONS;
  }, [showBreakthroughsOption]);

  const [userInteracted, setUserInteracted] = useState(false);
  useEffect(() => {
    if (value !== null) setUserInteracted(true);
    if (companyIsP2P)
      finalOptions[showBreakthroughsOption ? 5 : 4].label = 'Workshops';
  }, [value]);

  const selectedOption = useMemo(() => {
    if (value == null) return null;
    const found =
      finalOptions.find((o) => {
        if (Array.isArray(o.value) && Array.isArray(value)) {
          return JSON.stringify(o.value) === JSON.stringify(value);
        }
        return o.value === value;
      }) || null;

    if (found?.value === 'all' && !userInteracted) return null;
    return found;
  }, [value, userInteracted]);

  const handleChange = (_, option) => {
    const nextVal = option ? option.value : null;
    onChange?.(nextVal);
  };

  const handleClickMenu = (option) => {
    onChange?.(option.value);
  };

  if (!isDesktop) {
    return (
      <FormControl fullWidth={fullWidth} sx={sx}>
        <Autocomplete
          id="board-activity-filter"
          options={finalOptions}
          value={selectedOption}
          onChange={handleChange}
          getOptionLabel={(o) => o?.label ?? ''}
          isOptionEqualToValue={(opt, val) => {
            if (!opt || !val) return false;
            if (Array.isArray(opt.value) && Array.isArray(val.value)) {
              return JSON.stringify(opt.value) === JSON.stringify(val.value);
            }
            return opt.value === val.value;
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Filter Activities"
              size={size}
              InputProps={{
                ...params.InputProps,
                style: {
                  borderRadius: '15px',
                  height: size === 'small' ? 44 : 54,
                  backgroundColor: 'white',
                },
              }}
            />
          )}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#E6EDFF' },
              '&:hover fieldset': { borderColor: '#E6EDFF' },
              '&.Mui-focused fieldset': { borderColor: '#0F78FD' },
              '& input::placeholder': { color: '#74879A', opacity: 1 },
            },
            borderRadius: '15px',
            ...sx,
          }}
          disablePortal
          blurOnSelect
          clearOnBlur={false}
        />
      </FormControl>
    );
  }

  return (
    <Box
      sx={{
        width: fullWidth ? '100%' : 'auto',
        ...sx,
      }}
    >
      <Stack
        direction="row"
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          backgroundColor: '#F5F8FF',
          borderRadius: '12px',
          pb: 1,
        }}
      >
        {finalOptions.map((opt) => {
          const isActive = (() => {
            if (selectedOption == null) return opt.value === 'all';
            if (
              Array.isArray(opt.value) &&
              Array.isArray(selectedOption.value)
            ) {
              return (
                JSON.stringify(opt.value) ===
                JSON.stringify(selectedOption.value)
              );
            }
            return opt.value === selectedOption.value;
          })();

          return (
            <Button
              key={Array.isArray(opt.value) ? opt.value.join(',') : opt.value}
              onClick={() => handleClickMenu(opt)}
              disableRipple
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 14,
                borderRadius: '8px',
                px: '20px',
                minHeight: 48,
                backgroundColor: isActive ? '#DEE9FE' : 'transparent',
                color: isActive ? '#0A2540' : '#5B6C80',
                '&:hover': {
                  backgroundColor: isActive ? '#DEE9FE' : 'transparent',
                },
              }}
            >
              {opt.label}
            </Button>
          );
        })}
      </Stack>
    </Box>
  );
}
